import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { editRequirement } from '../../services/requirementService';
import { getDecryptedData } from '../../utils/authStorage';
import { toDateInputValue } from '../../utils/dateFormat';
import { normalizeRequirementType, REQUIREMENT_TYPE_OPTIONS } from '../../utils/requirementType';
import { CONTRACT_TYPE_OPTIONS } from '../../utils/requirementContractType';
import {
  MIN_ASSESSMENTS,
  appendRequirementFormData,
  padAssessments,
  validateAssessmentsInput,
} from '../../utils/requirementAssessments';
import RequirementJdActions from './RequirementJdActions';
import { showSuccess, showError } from '../../utils/toast';
import { getApiErrorMessage } from '../../utils/apiError';
import RequirementClientSelect from './RequirementClientSelect';

function buildFormData(requirement = {}) {
  return {
    id: requirement._id || '',
    client: requirement.client || '',
    clientId: requirement.clientId ? String(requirement.clientId) : '',
    typeOfContract: requirement.typeOfContract || '',
    startDate: toDateInputValue(requirement.startDate),
    duration: requirement.duration || '',
    location: requirement.location || '',
    sourceCtc: requirement.sourceCtc || '',
    ectc: requirement.ectc || '',
    qualification: requirement.qualification || '',
    yearsExperience: requirement.yearsExperience || '',
    relevantExperience: requirement.relevantExperience || '',
    skill: requirement.skill || '',
    role: requirement.role || '',
    requirementtype: normalizeRequirementType(requirement.requirementtype) || '',
    numberOfPositions: requirement.numberOfPositions ?? 1,
    assessments: padAssessments(requirement.assessments),
    jdPdf: requirement.jdPdf || '',
    jdPdfOriginalName: requirement.jdPdfOriginalName || '',
    uploadedBy: requirement.uploadedBy || getDecryptedData('Id') || '',
    update: 'Old',
  };
}

function RequirementEditModal({ show, onHide, requirement, onSaved, mode = 'teamlead' }) {
  const [formData, setFormData] = useState(() => buildFormData(requirement));
  const [jdFile, setJdFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && requirement) {
      setFormData(buildFormData(requirement));
      setJdFile(null);
    }
  }, [show, requirement]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientChange = ({ clientId, client }) => {
    setFormData((prev) => ({ ...prev, clientId, client }));
  };

  const handleAssessmentChange = (index, event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const assessments = [...prev.assessments];
      assessments[index] = { ...assessments[index], [name]: value };
      return { ...prev, assessments };
    });
  };

  const addAssessment = () => {
    setFormData((prev) => ({
      ...prev,
      assessments: [...prev.assessments, { assessment: '', yoe: '' }],
    }));
  };

  const deleteAssessment = (index) => {
    if (formData.assessments.length <= MIN_ASSESSMENTS) {
      showError(`Minimum ${MIN_ASSESSMENTS} assessments are required.`);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      assessments: prev.assessments.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleJdChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setJdFile(null);
      return;
    }
    if (file.type !== 'application/pdf') {
      showError('JD must be a PDF file.');
      event.target.value = '';
      setJdFile(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError('JD PDF must be 5 MB or smaller.');
      event.target.value = '';
      setJdFile(null);
      return;
    }
    setJdFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.id) return;

    const assessmentValidation = validateAssessmentsInput(formData.assessments);
    if (!assessmentValidation.ok) {
      showError(assessmentValidation.message);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        assessments: assessmentValidation.assessments,
        update: 'Old',
      };
      delete payload.jdPdf;
      delete payload.jdPdfOriginalName;
      delete payload.userUploadSettings;
      delete payload.assignedMembers;

      const multipart = new FormData();
      appendRequirementFormData(multipart, payload, jdFile);
      const updated = await editRequirement(formData.id, multipart);
      showSuccess('Requirement updated successfully ✓');
      onSaved?.(updated);
      onHide();
    } catch (error) {
      showError(getApiErrorMessage(error, 'Error updating requirement.'));
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = { width: '100%', padding: '10px', borderRadius: '5px' };

  return (
    <Modal size="lg" centered scrollable className="hr-form-modal" show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Requirement</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id="tl-edit-requirement-form" onSubmit={handleSubmit}>
          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-client"><strong>Client</strong></label>
              <RequirementClientSelect
                id="edit-client"
                mode={mode}
                clientId={formData.clientId}
                clientName={formData.client}
                onChange={handleClientChange}
                style={fieldStyle}
              />
            </div>
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-typeOfContract"><strong>Type of Contract *</strong></label>
              <select
                id="edit-typeOfContract"
                name="typeOfContract"
                value={formData.typeOfContract}
                onChange={handleChange}
                required
                style={fieldStyle}
              >
                <option value="">Select Contract Type</option>
                {CONTRACT_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-startDate"><strong>Start Date</strong></label>
              <input
                id="edit-startDate"
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-duration"><strong>Duration</strong></label>
              <input
                id="edit-duration"
                type="text"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-numberOfPositions"><strong>Number of Positions</strong></label>
              <input
                id="edit-numberOfPositions"
                type="number"
                min="1"
                name="numberOfPositions"
                value={formData.numberOfPositions ?? 1}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-location"><strong>Location</strong></label>
              <input
                id="edit-location"
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-sourceCtc"><strong>Source CTC</strong></label>
              <input
                id="edit-sourceCtc"
                type="text"
                name="sourceCtc"
                value={formData.sourceCtc}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-ectc"><strong>ECTC</strong></label>
              <input
                id="edit-ectc"
                type="text"
                name="ectc"
                value={formData.ectc}
                onChange={handleChange}
                style={fieldStyle}
                placeholder="Enter ECTC"
              />
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-qualification"><strong>Qualification</strong></label>
              <input
                id="edit-qualification"
                type="text"
                name="qualification"
                value={formData.qualification}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-yearsExperience"><strong>Years of Experience</strong></label>
              <input
                id="edit-yearsExperience"
                type="text"
                name="yearsExperience"
                value={formData.yearsExperience}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-relevantExperience"><strong>Relevant Experience</strong></label>
              <input
                id="edit-relevantExperience"
                type="text"
                name="relevantExperience"
                value={formData.relevantExperience}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-requirementtype"><strong>Requirement Type</strong></label>
              <select
                id="edit-requirementtype"
                name="requirementtype"
                value={formData.requirementtype}
                onChange={handleChange}
                required
                style={fieldStyle}
              >
                <option value="">Select Requirement Type</option>
                {REQUIREMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-skill"><strong>Skill</strong></label>
              <input
                id="edit-skill"
                type="text"
                name="skill"
                value={formData.skill}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
            <div className="flex-fill" style={{ minWidth: '240px' }}>
              <label htmlFor="edit-role"><strong>Role</strong></label>
              <input
                id="edit-role"
                type="text"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                style={fieldStyle}
              />
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="edit-jdPdf"><strong>JD PDF</strong></label>
            {formData.jdPdf ? (
              <div className="mb-2">
                <RequirementJdActions requirement={requirement} />
              </div>
            ) : (
              <div className="text-muted mb-2">No JD uploaded yet.</div>
            )}
            <input
              id="edit-jdPdf"
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleJdChange}
              style={fieldStyle}
            />
            <div className="text-muted mt-1">
              {formData.jdPdf
                ? 'Upload a new PDF to replace the current JD (optional).'
                : 'Optional. PDF only, maximum 5 MB.'}
            </div>
          </div>

          <div className="mb-3">
            <hr />
            <h5 className="text-center mb-1">Assessment *</h5>
            <div className="text-center text-muted mb-3">
              Minimum {MIN_ASSESSMENTS} assessments are mandatory.
            </div>
            {formData.assessments.map((assessment, index) => (
              <div key={index} className="d-flex flex-wrap gap-3 mb-2 align-items-end">
                <div className="flex-fill" style={{ minWidth: '240px' }}>
                  <label htmlFor={`edit-assessment-${index}`}><strong>Assessment</strong></label>
                  <input
                    id={`edit-assessment-${index}`}
                    type="text"
                    name="assessment"
                    value={assessment.assessment}
                    onChange={(event) => handleAssessmentChange(index, event)}
                    style={fieldStyle}
                    required
                  />
                </div>
                <div className="flex-fill" style={{ minWidth: '240px' }}>
                  <label htmlFor={`edit-yoe-${index}`}><strong>Expected Rating</strong></label>
                  <input
                    id={`edit-yoe-${index}`}
                    type="text"
                    name="yoe"
                    value={assessment.yoe}
                    onChange={(event) => handleAssessmentChange(index, event)}
                    style={fieldStyle}
                    required
                  />
                </div>
                {formData.assessments.length > MIN_ASSESSMENTS && (
                  <Button
                    type="button"
                    variant="outline-danger"
                    onClick={() => deleteAssessment(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline-success" onClick={addAssessment}>
              Add Assessment
            </Button>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="hr-form-modal-footer">
        <Button variant="secondary" className="hr-btn-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          className="hr-btn-primary"
          disabled={saving}
          onClick={() => document.getElementById('tl-edit-requirement-form')?.requestSubmit()}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RequirementEditModal;
